import { getAccountManagersRequest, parseAccountManagers } from 'tio-data/requests/company';
import type { AccountManager } from 'tio-data/requests/company';

/**
 * Dedupes GET /companies/:id/account-managers by company id, for the life of
 * the page. `getAccountManagersRequest` opts out of the store's document
 * cache (its payload's resource type doesn't bind to the singular schema —
 * see the builder's own doc comment), so without this every eligibility-card
 * instance issues its own request; the admin list can render up to 50 cards
 * per page, many sharing a company.
 */

interface StoreLike {
  request(req: ReturnType<typeof getAccountManagersRequest>): Promise<{ content: unknown }>;
}

const managersCache = new Map<string, AccountManager[]>();
const inFlight = new Map<string, Promise<AccountManager[]>>();

export function fetchAccountManagersCached(
  store: StoreLike,
  companyId: string
): Promise<AccountManager[]> {
  const cached = managersCache.get(companyId);
  if (cached) return Promise.resolve(cached);

  const existing = inFlight.get(companyId);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const response = await store.request(getAccountManagersRequest(companyId));
      const managers = parseAccountManagers(
        response.content as Parameters<typeof parseAccountManagers>[0]
      );
      managersCache.set(companyId, managers);
      return managers;
    } finally {
      inFlight.delete(companyId);
    }
  })();

  inFlight.set(companyId, promise);
  return promise;
}

/** Test hook: reset the cached/in-flight account-manager lookups. */
export function clearAccountManagersCache() {
  managersCache.clear();
  inFlight.clear();
}
